import React from 'react';
import { View, Text } from '@react-pdf/renderer';
import { typographyStyles } from '../styles/typography';
import { formatDateForPDF } from './dataFormatters';
import { createT } from './simpleTranslate';
import { PDFSurveyRecordRenderer } from './contentRenderer';
import { PDFSmartFieldRenderer } from './PDFSmartFieldRenderer';
import { isSmartField } from '../../../utils/index.jsx';
import { describeDiagnosisValue } from '../../../utils/icd11Display';

/**
 * Unified PDF Field Renderer
 * 
 * Handles rendering of all field types in PDF documents.
 * This provides a consistent way to render fields across all PDFs.
 */

interface PDFFieldRendererProps {
  field: {
    id: string | number;
    name: string;
    label: string;
    type: string;
    order?: number;
    options?: string[] | any[];
    /** Set on a field whose options are another field's picks (`lesion_diagnoses`). */
    forwardedFrom?: string;
  };
  value: any;
  locale?: string;
  /**
   * SKIN-612 — code -> disease name from the ICD-11 picks of the same submission
   * (`diagnosisLabelsFromRecords`), for codes stored without a label.
   */
  diagnosisLabels?: ReadonlyMap<string, string>;
}

export const PDFFieldRenderer: React.FC<PDFFieldRendererProps> = ({
  field,
  value,
  locale = 'fr',
  diagnosisLabels,
}) => {
  const t = createT(locale);

  // Handle empty/null values
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return (
      <Text style={typographyStyles.body2}>
        {isSmartField(field.type) ? t('general.no', 'Non') : '-'}
      </Text>
    );
  }

  // SKIN-612: an ICD-11 pick and a forwarded `lesion_diagnoses` are lists of coded
  // entries; printed raw they read "[object Object]". Each code carries its name.
  if (field.type === 'icd11-code' || field.forwardedFrom) {
    const codes = describeDiagnosisValue(value, diagnosisLabels ?? new Map());
    if (codes.length > 0) {
      return (
        <Text style={typographyStyles.body2}>
          {codes.join(', ')}
        </Text>
      );
    }
  }

  // Handle different field types
  switch (field.type) {
    case 'textarea':
    case 'medical_history_template':
    case 'medical_history_ai':
      // HTML content - use HTML renderer
      const htmlContent = typeof value === 'string' ? value : 
        (Array.isArray(value) && value.length > 0 ? 
          value[0].medical_history_ai || value[0]['template_medical_history-notes'] || '' : '');
      
      return (
        <PDFSurveyRecordRenderer 
          recordValue={htmlContent}
          fallbackStrategy="plaintext"
        />
      );

    case 'date':
      const dateValue = typeof value === 'string' ? new Date(value) : value;
      return (
        <Text style={typographyStyles.body2}>
          {formatDateForPDF(dateValue, locale)}
        </Text>
      );

    case 'multioption':
      const options = Array.isArray(value) ? value.join(', ') : value;
      return (
        <Text style={typographyStyles.body2}>
          {options}
        </Text>
      );

    case 'file':
      return (
        <Text style={typographyStyles.body2}>
          {t('general.file_attached', 'Fichier joint')}
        </Text>
      );

    default:
      // Handle smart fields
      if (isSmartField(field.type)) {
        return (
          <PDFSmartFieldRenderer
            type={field.type}
            value={value}
            locale={locale}
          />
        );
      }
      
      // Regular text/number fields
      return (
        <Text style={typographyStyles.body2}>
          {String(value)}
        </Text>
      );
  }
};

export default PDFFieldRenderer;

